import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CreditCard,
  Plus,
  Pencil,
  Trash2,
  Star,
  Check,
  RefreshCw,
  DollarSign,
  Users,
  Clock,
  Phone,
  Target
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminPlans() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const defaultPlan = {
    name: "",
    slug: "",
    description: "",
    price_monthly: 0,
    price_yearly: null,
    currency: "USD",
    max_agents: 1,
    max_minutes_monthly: 100,
    max_contacts: 500,
    max_campaigns: 3,
    features: [],
    is_active: true,
    is_featured: false,
    trial_days: 0,
    sort_order: 0
  };

  const [formData, setFormData] = useState(defaultPlan);
  const [newFeature, setNewFeature] = useState("");

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/plans?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPlans(data);
      }
    } catch (error) {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData(defaultPlan);
    setEditingPlan(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setFormData({
      ...plan,
      features: plan.features || []
    });
    setEditingPlan(plan);
    setIsDialogOpen(true);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingPlan ? prev.slug : generateSlug(name)
    }));
  };

  const addFeature = () => {
    if (newFeature && !formData.features.includes(newFeature)) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature]
      }));
      setNewFeature("");
    }
  };

  const removeFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const savePlan = async () => {
    if (!formData.name || !formData.slug) {
      toast.error("Name and slug are required");
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const url = editingPlan 
        ? `${API_URL}/api/admin/settings/plans/${editingPlan.id}`
        : `${API_URL}/api/admin/settings/plans`;
      
      const res = await fetch(url, {
        method: editingPlan ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingPlan ? "Plan updated" : "Plan created");
        setIsDialogOpen(false);
        loadPlans();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to save plan");
      }
    } catch (error) {
      toast.error("Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!confirm("Are you sure you want to deactivate this plan?")) return;
    
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/plans/${planId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Plan deactivated");
        loadPlans();
      } else {
        toast.error("Failed to deactivate plan");
      }
    } catch (error) {
      toast.error("Failed to deactivate plan");
    }
  };

  const formatLimit = (value) => {
    if (value === -1) return "Unlimited";
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Subscription Plans</h1>
          <p className="text-slate-500 mt-1">Manage pricing and features for your plans</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadPlans} data-testid="refresh-plans">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} data-testid="create-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Plan
          </Button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <Card 
            key={plan.id || plan.slug}
            className={`relative ${!plan.is_active ? "opacity-60" : ""} ${plan.is_featured ? "ring-2 ring-blue-500" : ""}`}
            data-testid={`plan-card-${plan.slug}`}
          >
            {plan.is_featured && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-blue-500 text-white">
                  <Star className="w-3 h-3 mr-1" fill="currentColor" /> Popular
                </Badge>
              </div>
            )}
            
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  {!plan.is_active && (
                    <Badge variant="secondary" className="mt-1">Inactive</Badge>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEditDialog(plan)}
                    data-testid={`edit-plan-${plan.slug}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {plan.id !== "free_trial" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500 hover:text-red-700"
                      onClick={() => deletePlan(plan.id)}
                      data-testid={`delete-plan-${plan.slug}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="pt-2">
                <span className="text-3xl font-bold">${plan.price_monthly}</span>
                <span className="text-slate-500">/month</span>
                {plan.price_yearly && (
                  <p className="text-sm text-slate-500 mt-1">
                    or ${plan.price_yearly}/year (save ${plan.price_monthly * 12 - plan.price_yearly})
                  </p>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              {plan.description && (
                <p className="text-sm text-slate-600">{plan.description}</p>
              )}
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Users className="w-4 h-4" />
                  <span>{formatLimit(plan.max_agents)} Agents</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="w-4 h-4" />
                  <span>{formatLimit(plan.max_minutes_monthly)} min/month</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4" />
                  <span>{formatLimit(plan.max_contacts)} Contacts</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Target className="w-4 h-4" />
                  <span>{formatLimit(plan.max_campaigns)} Campaigns</span>
                </div>
              </div>
              
              {plan.features && plan.features.length > 0 && (
                <div className="pt-2 border-t space-y-1">
                  {plan.features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{feature}</span>
                    </div>
                  ))}
                  {plan.features.length > 4 && (
                    <p className="text-xs text-slate-400 pl-6">+{plan.features.length - 4} more</p>
                  )}
                </div>
              )}
              
              {plan.trial_days > 0 && (
                <Badge variant="outline" className="w-full justify-center">
                  {plan.trial_days} day free trial
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? "Edit Plan" : "Create New Plan"}</DialogTitle>
            <DialogDescription>
              {editingPlan ? "Update the plan details below" : "Fill in the details for the new subscription plan"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Professional"
                  data-testid="plan-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="e.g., professional"
                  data-testid="plan-slug-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this plan"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price_monthly">Monthly Price ($)</Label>
                <Input
                  id="price_monthly"
                  type="number"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                  data-testid="plan-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_yearly">Yearly Price ($)</Label>
                <Input
                  id="price_yearly"
                  type="number"
                  value={formData.price_yearly || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || null }))}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trial_days">Trial Days</Label>
                <Input
                  id="trial_days"
                  type="number"
                  value={formData.trial_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max_agents">Max Agents</Label>
                <Input
                  id="max_agents"
                  type="number"
                  value={formData.max_agents}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_agents: parseInt(e.target.value) || 0 }))}
                />
                <p className="text-xs text-slate-500">-1 = unlimited</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_minutes">Max Minutes</Label>
                <Input
                  id="max_minutes"
                  type="number"
                  value={formData.max_minutes_monthly}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_minutes_monthly: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_contacts">Max Contacts</Label>
                <Input
                  id="max_contacts"
                  type="number"
                  value={formData.max_contacts}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_contacts: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_campaigns">Max Campaigns</Label>
                <Input
                  id="max_campaigns"
                  type="number"
                  value={formData.max_campaigns}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_campaigns: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Features</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.features.map((feature, idx) => (
                  <Badge key={idx} variant="outline" className="px-2 py-1">
                    {feature}
                    <button
                      onClick={() => removeFeature(feature)}
                      className="ml-2 text-slate-400 hover:text-red-500"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="Add a feature"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                />
                <Button type="button" variant="outline" onClick={addFeature} size="sm">
                  Add
                </Button>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                />
                <Label htmlFor="is_featured">Featured (Popular badge)</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={savePlan} disabled={saving} data-testid="save-plan-btn">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              {editingPlan ? "Update Plan" : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
